import { useReducer, useCallback, useMemo } from 'react';
import { BIBLE_BOOKS } from '../utils/bibleData';

// Initial state - exactly matching current useState structure
const initialState = {
  // Manual selector state
  selectedBook: 'genesis',
  selectedChapter: 1,
  startVerse: 1,
  endVerse: 1,
  availableChapters: [],
  availableVerses: [],
  currentPassageText: '',
  
  // Recorded passages state
  recordedPassages: [],
  search: '',
  filteredPassages: [],
  selectedRecordedPassage: null,
  
  // Audio state
  matchingRecordings: [],
  audioLoading: false,
  currentlyPlayingVerse: null,
  isLoadingRecordings: false,
  hasSearchedForRecordings: false,
  isPlaying: false,
  
  // UI state for collapsible menus
  showPassageSelector: false,
  showFamilyDropdown: false,
  showReaderDropdown: false,
  showPlanDropdown: false,
  showFilters: false,
  showSearchAccordion: false,
  
  // Family membership state
  userFamilyIds: [],
  userFamilies: [],
  loading: true,
  isInitializing: true,
  
  // Filter state
  selectedFamily: 'all',
  selectedReaders: [],
  availableReaders: [],
  
  // Listening plan state
  currentListeningPlan: null,
  availableListeningPlans: [],
  selectedPlan: 'all',
  
  // Study Notes state
  hasNotesForChapter: false,
  showStudyNoteEditor: false,
  showNotesDrawer: false,
  verseNotes: {},
  noteForVerse: null,
  
  // Verse action accordion state
  showVerseAccordion: false,
  selectedVerseForAction: null,
  favoritedVerses: new Set(),
};

// Fast (synchronous) cache of the last listen point so the Listen page opens
// directly there instead of flashing Genesis 1 before async loads/overrides run.
export const LISTEN_POINT_KEY = 'fb_listenPoint';
export const readListenPoint = () => {
  try {
    return JSON.parse(localStorage.getItem(LISTEN_POINT_KEY) || 'null');
  } catch {
    return null;
  }
};

// Action types
export const ACTIONS = {
  // Passage selection
  SET_SELECTED_BOOK: 'SET_SELECTED_BOOK',
  SET_SELECTED_CHAPTER: 'SET_SELECTED_CHAPTER',
  SET_START_VERSE: 'SET_START_VERSE',
  SET_END_VERSE: 'SET_END_VERSE',
  SET_VERSE_RANGE: 'SET_VERSE_RANGE',
  SET_AVAILABLE_CHAPTERS: 'SET_AVAILABLE_CHAPTERS',
  SET_AVAILABLE_VERSES: 'SET_AVAILABLE_VERSES',
  SET_CURRENT_PASSAGE_TEXT: 'SET_CURRENT_PASSAGE_TEXT',
  
  // Recorded passages
  SET_RECORDED_PASSAGES: 'SET_RECORDED_PASSAGES',
  SET_SEARCH: 'SET_SEARCH',
  SET_FILTERED_PASSAGES: 'SET_FILTERED_PASSAGES',
  SET_SELECTED_RECORDED_PASSAGE: 'SET_SELECTED_RECORDED_PASSAGE',
  
  // Audio
  SET_MATCHING_RECORDINGS: 'SET_MATCHING_RECORDINGS',
  SET_AUDIO_LOADING: 'SET_AUDIO_LOADING',
  SET_CURRENTLY_PLAYING_VERSE: 'SET_CURRENTLY_PLAYING_VERSE',
  SET_IS_LOADING_RECORDINGS: 'SET_IS_LOADING_RECORDINGS',
  SET_HAS_SEARCHED_FOR_RECORDINGS: 'SET_HAS_SEARCHED_FOR_RECORDINGS',
  SET_IS_PLAYING: 'SET_IS_PLAYING',
  
  // UI toggles
  TOGGLE_PASSAGE_SELECTOR: 'TOGGLE_PASSAGE_SELECTOR',
  TOGGLE_FAMILY_DROPDOWN: 'TOGGLE_FAMILY_DROPDOWN',
  TOGGLE_READER_DROPDOWN: 'TOGGLE_READER_DROPDOWN',
  TOGGLE_PLAN_DROPDOWN: 'TOGGLE_PLAN_DROPDOWN',
  TOGGLE_FILTERS: 'TOGGLE_FILTERS',
  TOGGLE_SEARCH_ACCORDION: 'TOGGLE_SEARCH_ACCORDION',
  CLOSE_ALL_DROPDOWNS: 'CLOSE_ALL_DROPDOWNS',
  
  // Family data
  SET_USER_FAMILY_IDS: 'SET_USER_FAMILY_IDS',
  SET_USER_FAMILIES: 'SET_USER_FAMILIES',
  SET_LOADING: 'SET_LOADING',
  SET_IS_INITIALIZING: 'SET_IS_INITIALIZING',
  
  // Filters
  SET_SELECTED_FAMILY: 'SET_SELECTED_FAMILY',
  SET_SELECTED_READER: 'SET_SELECTED_READER',
  SET_AVAILABLE_READERS: 'SET_AVAILABLE_READERS',
  
  // Plans
  SET_CURRENT_LISTENING_PLAN: 'SET_CURRENT_LISTENING_PLAN',
  SET_AVAILABLE_LISTENING_PLANS: 'SET_AVAILABLE_LISTENING_PLANS',
  SET_SELECTED_PLAN: 'SET_SELECTED_PLAN',
  
  // Study notes
  SET_HAS_NOTES_FOR_CHAPTER: 'SET_HAS_NOTES_FOR_CHAPTER',
  SET_SHOW_STUDY_NOTE_EDITOR: 'SET_SHOW_STUDY_NOTE_EDITOR',
  SET_SHOW_NOTES_DRAWER: 'SET_SHOW_NOTES_DRAWER',
  SET_VERSE_NOTES: 'SET_VERSE_NOTES',
  SET_NOTE_FOR_VERSE: 'SET_NOTE_FOR_VERSE',
  
  // Verse actions
  SET_SHOW_VERSE_ACCORDION: 'SET_SHOW_VERSE_ACCORDION',
  SET_SELECTED_VERSE_FOR_ACTION: 'SET_SELECTED_VERSE_FOR_ACTION',
  SET_FAVORITED_VERSES: 'SET_FAVORITED_VERSES',
  ADD_FAVORITE_VERSE: 'ADD_FAVORITE_VERSE',
  REMOVE_FAVORITE_VERSE: 'REMOVE_FAVORITE_VERSE',
  
  // Batch updates
  BATCH_UPDATE: 'BATCH_UPDATE',
};

// Reducer function
function listenReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_SELECTED_BOOK:
      return { ...state, selectedBook: action.payload };
    
    case ACTIONS.SET_SELECTED_CHAPTER:
      return { ...state, selectedChapter: action.payload };
    
    case ACTIONS.SET_START_VERSE:
      return { ...state, startVerse: action.payload };
    
    case ACTIONS.SET_END_VERSE:
      return { ...state, endVerse: action.payload };
    
    case ACTIONS.SET_VERSE_RANGE:
      return { 
        ...state, 
        startVerse: action.payload.start, 
        endVerse: action.payload.end 
      };
    
    case ACTIONS.SET_AVAILABLE_CHAPTERS:
      return { ...state, availableChapters: action.payload };
    
    case ACTIONS.SET_AVAILABLE_VERSES:
      return { ...state, availableVerses: action.payload };
    
    case ACTIONS.SET_CURRENT_PASSAGE_TEXT:
      return { ...state, currentPassageText: action.payload };
    
    case ACTIONS.SET_RECORDED_PASSAGES:
      return { ...state, recordedPassages: action.payload };
    
    case ACTIONS.SET_SEARCH:
      return { ...state, search: action.payload };
    
    case ACTIONS.SET_FILTERED_PASSAGES:
      return { ...state, filteredPassages: action.payload };
    
    case ACTIONS.SET_SELECTED_RECORDED_PASSAGE:
      return { ...state, selectedRecordedPassage: action.payload };
    
    case ACTIONS.SET_MATCHING_RECORDINGS:
      return { ...state, matchingRecordings: action.payload };
    
    case ACTIONS.SET_AUDIO_LOADING:
      return { ...state, audioLoading: action.payload };
    
    case ACTIONS.SET_CURRENTLY_PLAYING_VERSE:
      return { ...state, currentlyPlayingVerse: action.payload };
    
    case ACTIONS.SET_IS_LOADING_RECORDINGS:
      return { ...state, isLoadingRecordings: action.payload };
    
    case ACTIONS.SET_HAS_SEARCHED_FOR_RECORDINGS:
      return { ...state, hasSearchedForRecordings: action.payload };
    
    case ACTIONS.SET_IS_PLAYING:
      return { ...state, isPlaying: action.payload };
    
    case ACTIONS.TOGGLE_PASSAGE_SELECTOR:
      return { ...state, showPassageSelector: !state.showPassageSelector };
    
    case ACTIONS.TOGGLE_FAMILY_DROPDOWN:
      return { ...state, showFamilyDropdown: !state.showFamilyDropdown };
    
    case ACTIONS.TOGGLE_READER_DROPDOWN:
      return { ...state, showReaderDropdown: !state.showReaderDropdown };
    
    case ACTIONS.TOGGLE_PLAN_DROPDOWN:
      return { ...state, showPlanDropdown: !state.showPlanDropdown };
    
    case ACTIONS.TOGGLE_FILTERS:
      return { ...state, showFilters: !state.showFilters };
    
    case ACTIONS.TOGGLE_SEARCH_ACCORDION:
      return { ...state, showSearchAccordion: !state.showSearchAccordion };
    
    case ACTIONS.CLOSE_ALL_DROPDOWNS:
      return {
        ...state,
        showPassageSelector: false,
        showFamilyDropdown: false,
        showReaderDropdown: false,
        showPlanDropdown: false,
        showFilters: false,
        showSearchAccordion: false,
      };
    
    case ACTIONS.SET_USER_FAMILY_IDS:
      return { ...state, userFamilyIds: action.payload };
    
    case ACTIONS.SET_USER_FAMILIES:
      return { ...state, userFamilies: action.payload };
    
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ACTIONS.SET_IS_INITIALIZING:
      return { ...state, isInitializing: action.payload };
    
    case ACTIONS.SET_SELECTED_FAMILY:
      return { ...state, selectedFamily: action.payload };
    
    case ACTIONS.SET_SELECTED_READER:
      return { ...state, selectedReaders: action.payload };
    
    case ACTIONS.SET_AVAILABLE_READERS:
      return { ...state, availableReaders: action.payload };
    
    case ACTIONS.SET_CURRENT_LISTENING_PLAN:
      return { ...state, currentListeningPlan: action.payload };
    
    case ACTIONS.SET_AVAILABLE_LISTENING_PLANS:
      return { ...state, availableListeningPlans: action.payload };
    
    case ACTIONS.SET_SELECTED_PLAN:
      return { ...state, selectedPlan: action.payload };
    
    case ACTIONS.SET_HAS_NOTES_FOR_CHAPTER:
      return { ...state, hasNotesForChapter: action.payload };
    
    case ACTIONS.SET_SHOW_STUDY_NOTE_EDITOR:
      return { ...state, showStudyNoteEditor: action.payload };
    
    case ACTIONS.SET_SHOW_NOTES_DRAWER:
      return { ...state, showNotesDrawer: action.payload };
    
    case ACTIONS.SET_VERSE_NOTES:
      return { ...state, verseNotes: action.payload };
    
    case ACTIONS.SET_NOTE_FOR_VERSE:
      return { ...state, noteForVerse: action.payload };
    
    case ACTIONS.SET_SHOW_VERSE_ACCORDION:
      return { ...state, showVerseAccordion: action.payload };
    
    case ACTIONS.SET_SELECTED_VERSE_FOR_ACTION:
      return { ...state, selectedVerseForAction: action.payload };
    
    case ACTIONS.SET_FAVORITED_VERSES:
      return { ...state, favoritedVerses: action.payload };
    
    case ACTIONS.ADD_FAVORITE_VERSE:
      const newFavorites = new Set(state.favoritedVerses);
      newFavorites.add(action.payload);
      return { ...state, favoritedVerses: newFavorites };
    
    case ACTIONS.REMOVE_FAVORITE_VERSE:
      const updatedFavorites = new Set(state.favoritedVerses);
      updatedFavorites.delete(action.payload);
      return { ...state, favoritedVerses: updatedFavorites };
    
    case ACTIONS.BATCH_UPDATE:
      return { ...state, ...action.payload };
    
    default:
      return state;
  }
}

// Custom hook for Listen state management
export function useListenState() {
  const [state, dispatch] = useReducer(listenReducer, initialState, (base) => {
    const p = readListenPoint();
    if (p && p.book) {
      return {
        ...base,
        selectedBook: p.book,
        selectedChapter: Number(p.chapter) || 1,
        startVerse: Number(p.startVerse) || 1,
        endVerse: Number(p.endVerse) || 1,
      };
    }
    return base;
  });
  
  // Memoized action creators for performance
  const actions = useMemo(() => ({
    // Passage selection
    setSelectedBook: (book) => dispatch({ type: ACTIONS.SET_SELECTED_BOOK, payload: book }),
    setSelectedChapter: (chapter) => dispatch({ type: ACTIONS.SET_SELECTED_CHAPTER, payload: chapter }),
    setStartVerse: (verse) => dispatch({ type: ACTIONS.SET_START_VERSE, payload: verse }),
    setEndVerse: (verse) => dispatch({ type: ACTIONS.SET_END_VERSE, payload: verse }),
    setVerseRange: (start, end) => dispatch({ type: ACTIONS.SET_VERSE_RANGE, payload: { start, end } }),
    setAvailableChapters: (chapters) => dispatch({ type: ACTIONS.SET_AVAILABLE_CHAPTERS, payload: chapters }),
    setAvailableVerses: (verses) => dispatch({ type: ACTIONS.SET_AVAILABLE_VERSES, payload: verses }),
    setCurrentPassageText: (text) => dispatch({ type: ACTIONS.SET_CURRENT_PASSAGE_TEXT, payload: text }),
    
    // Recorded passages
    setRecordedPassages: (passages) => dispatch({ type: ACTIONS.SET_RECORDED_PASSAGES, payload: passages }),
    setSearch: (search) => dispatch({ type: ACTIONS.SET_SEARCH, payload: search }),
    setFilteredPassages: (passages) => dispatch({ type: ACTIONS.SET_FILTERED_PASSAGES, payload: passages }),
    setSelectedRecordedPassage: (passage) => dispatch({ type: ACTIONS.SET_SELECTED_RECORDED_PASSAGE, payload: passage }),
    
    // Audio
    setMatchingRecordings: (recordings) => dispatch({ type: ACTIONS.SET_MATCHING_RECORDINGS, payload: recordings }),
    setAudioLoading: (loading) => dispatch({ type: ACTIONS.SET_AUDIO_LOADING, payload: loading }),
    setCurrentlyPlayingVerse: (verse) => dispatch({ type: ACTIONS.SET_CURRENTLY_PLAYING_VERSE, payload: verse }),
    setIsLoadingRecordings: (loading) => dispatch({ type: ACTIONS.SET_IS_LOADING_RECORDINGS, payload: loading }),
    setHasSearchedForRecordings: (searched) => dispatch({ type: ACTIONS.SET_HAS_SEARCHED_FOR_RECORDINGS, payload: searched }),
    setIsPlaying: (playing) => dispatch({ type: ACTIONS.SET_IS_PLAYING, payload: playing }),
    
    // UI toggles
    togglePassageSelector: () => dispatch({ type: ACTIONS.TOGGLE_PASSAGE_SELECTOR }),
    toggleFamilyDropdown: () => dispatch({ type: ACTIONS.TOGGLE_FAMILY_DROPDOWN }),
    toggleReaderDropdown: () => dispatch({ type: ACTIONS.TOGGLE_READER_DROPDOWN }),
    togglePlanDropdown: () => dispatch({ type: ACTIONS.TOGGLE_PLAN_DROPDOWN }),
    toggleFilters: () => dispatch({ type: ACTIONS.TOGGLE_FILTERS }),
    toggleSearchAccordion: () => dispatch({ type: ACTIONS.TOGGLE_SEARCH_ACCORDION }),
    closeAllDropdowns: () => dispatch({ type: ACTIONS.CLOSE_ALL_DROPDOWNS }),
    
    // Family data
    setUserFamilyIds: (ids) => dispatch({ type: ACTIONS.SET_USER_FAMILY_IDS, payload: ids }),
    setUserFamilies: (families) => dispatch({ type: ACTIONS.SET_USER_FAMILIES, payload: families }),
    setLoading: (loading) => dispatch({ type: ACTIONS.SET_LOADING, payload: loading }),
    setIsInitializing: (initializing) => dispatch({ type: ACTIONS.SET_IS_INITIALIZING, payload: initializing }),
    
    // Filters
    setSelectedFamily: (family) => dispatch({ type: ACTIONS.SET_SELECTED_FAMILY, payload: family }),
    setSelectedReaders: (readers) => dispatch({ type: ACTIONS.SET_SELECTED_READER, payload: readers }),
    setAvailableReaders: (readers) => dispatch({ type: ACTIONS.SET_AVAILABLE_READERS, payload: readers }),
    
    // Plans
    setCurrentListeningPlan: (plan) => dispatch({ type: ACTIONS.SET_CURRENT_LISTENING_PLAN, payload: plan }),
    setAvailableListeningPlans: (plans) => dispatch({ type: ACTIONS.SET_AVAILABLE_LISTENING_PLANS, payload: plans }),
    setSelectedPlan: (plan) => dispatch({ type: ACTIONS.SET_SELECTED_PLAN, payload: plan }),
    
    // Study notes
    setHasNotesForChapter: (hasNotes) => dispatch({ type: ACTIONS.SET_HAS_NOTES_FOR_CHAPTER, payload: hasNotes }),
    setShowStudyNoteEditor: (show) => dispatch({ type: ACTIONS.SET_SHOW_STUDY_NOTE_EDITOR, payload: show }),
    setShowNotesDrawer: (show) => dispatch({ type: ACTIONS.SET_SHOW_NOTES_DRAWER, payload: show }),
    setVerseNotes: (notes) => dispatch({ type: ACTIONS.SET_VERSE_NOTES, payload: notes }),
    setNoteForVerse: (verse) => dispatch({ type: ACTIONS.SET_NOTE_FOR_VERSE, payload: verse }),
    
    // Verse actions
    setShowVerseAccordion: (show) => dispatch({ type: ACTIONS.SET_SHOW_VERSE_ACCORDION, payload: show }),
    setSelectedVerseForAction: (verse) => dispatch({ type: ACTIONS.SET_SELECTED_VERSE_FOR_ACTION, payload: verse }),
    setFavoritedVerses: (verses) => dispatch({ type: ACTIONS.SET_FAVORITED_VERSES, payload: verses }),
    addFavoriteVerse: (verseKey) => dispatch({ type: ACTIONS.ADD_FAVORITE_VERSE, payload: verseKey }),
    removeFavoriteVerse: (verseKey) => dispatch({ type: ACTIONS.REMOVE_FAVORITE_VERSE, payload: verseKey }),
    
    // Batch updates
    batchUpdate: (updates) => dispatch({ type: ACTIONS.BATCH_UPDATE, payload: updates }),
    
    // Logical default navigation
    determineInitialChapter: (currentUser, currentListeningPlan, availableListeningPlans, recordedPassages) => {
      console.log('🎯 Determining initial chapter with logical defaults');
      
      // Priority 1: Navigate to next chapter in current listening plan
      if (currentListeningPlan && availableListeningPlans.length > 0) {
        const plan = availableListeningPlans.find(p => p.id === currentListeningPlan);
        if (plan && plan.chapters && plan.chapters.length > 0) {
          console.log('🎯 PRIORITY 1: Navigating to next chapter in current plan');
          const nextChapter = plan.chapters[0]; // First chapter in plan
          dispatch({ type: ACTIONS.SET_SELECTED_BOOK, payload: nextChapter.book });
          dispatch({ type: ACTIONS.SET_SELECTED_CHAPTER, payload: nextChapter.chapter });
          return;
        }
      }

      // Priority 2: Load next chapter with recorded verses
      if (recordedPassages && recordedPassages.length > 0) {
        console.log('🎯 PRIORITY 2: Looking for next chapter with recordings');
        
        // Group recordings by book and chapter
        const chapterGroups = {};
        recordedPassages.forEach(passage => {
          const key = `${passage.book}_${passage.chapter}`;
          if (!chapterGroups[key]) {
            chapterGroups[key] = {
              book: passage.book,
              chapter: passage.chapter,
              count: 0
            };
          }
          chapterGroups[key].count++;
        });

        // Find the first chapter with recordings
        const chaptersWithRecordings = Object.values(chapterGroups).sort((a, b) => {
          // Sort by book order, then by chapter
          const bookOrder = BIBLE_BOOKS.findIndex(book => book.id === a.book) - 
                           BIBLE_BOOKS.findIndex(book => book.id === b.book);
          if (bookOrder !== 0) return bookOrder;
          return a.chapter - b.chapter;
        });

        if (chaptersWithRecordings.length > 0) {
          const firstChapter = chaptersWithRecordings[0];
          console.log(`🎯 Found first chapter with recordings: ${firstChapter.book} ${firstChapter.chapter} (${firstChapter.count} recordings)`);
          dispatch({ type: ACTIONS.SET_SELECTED_BOOK, payload: firstChapter.book });
          dispatch({ type: ACTIONS.SET_SELECTED_CHAPTER, payload: firstChapter.chapter });
          return;
        }
      }

      console.log('🎯 No recordings found, staying with default: genesis 1');
    },
  }), []);
  
  return { state, actions };
}
